/** Balance, bet, and win displays — assembled from actual game sprite assets */

import { Container, Text, TextStyle, Sprite, Texture, Assets } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('HUD');

export class HUD extends Container {
  private _balanceText: Text;
  private _betText: Text;
  private _winText: Text;
  private _winDetailText: Text;
  private _panelWidth: number;

  constructor(panelWidth: number = 1200) {
    super();
    this._panelWidth = panelWidth;

    // The HUD sits directly below the frame.
    // y=0 in this container = the top of the HUD = where the frame ends.
    //
    // Layout (all y relative to 0):
    //   y=-shadow_height..0  : shadow gradient (extends upward behind frame)
    //   y=0..barHeight       : gold bar
    //   y=0..barHeight       : panels (total_bet_bg on left/right, win_bg center) ON TOP of gold bar
    //   text inside panels

    const barHeight = 100; // effective gold bar height in local coords

    // Panel vertical bounds — panels stretch from top to bottom of bar
    const panelTop = 5;
    const panelBottom = barHeight - 5;
    const panelMidY = (panelTop + panelBottom) / 2;

    // Text positions INSIDE panels
    const valueY = panelMidY - 10;  // value in upper part of panel
    const labelY = panelMidY + 18;  // label in lower part of panel

    // Column X positions
    const leftX = panelWidth * 0.15;
    const centerX = panelWidth / 2;
    const rightX = panelWidth * 0.85;

    // ================================================================
    // TEXT STYLES
    // ================================================================
    const valueStyle = new TextStyle({
      fontFamily: 'Arial Black, Impact, Arial',
      fontSize: 44,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: 'bold',
    });

    const winValueStyle = new TextStyle({
      fontFamily: 'Arial Black, Impact, Arial',
      fontSize: 56,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 4 },
      fontWeight: 'bold',
    });

    const redLabelStyle = new TextStyle({
      fontFamily: 'Arial, Helvetica',
      fontSize: 18,
      fill: 0xe04040,
      fontWeight: 'bold',
      letterSpacing: 2,
    });

    const winLabelStyle = new TextStyle({
      fontFamily: 'Arial, Helvetica',
      fontSize: 20,
      fill: 0xe04040,
      fontWeight: 'bold',
      letterSpacing: 3,
    });

    const winDetailStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 16,
      fill: 0xffdd88,
      stroke: { color: 0x000000, width: 2 },
    });

    // ================================================================
    // TEXT — all INSIDE panels
    // ================================================================

    // TOTAL BET (left)
    this._betText = new Text({ text: '$2.50', style: valueStyle });
    this._betText.anchor.set(0.5, 0.5);
    this._betText.x = leftX;
    this._betText.y = valueY;
    this.addChild(this._betText);

    const betLabel = new Text({ text: 'TOTAL BET', style: redLabelStyle });
    betLabel.anchor.set(0.5, 0.5);
    betLabel.x = leftX;
    betLabel.y = labelY;
    this.addChild(betLabel);

    // WIN (center)
    this._winText = new Text({ text: '', style: winValueStyle });
    this._winText.anchor.set(0.5, 0.5);
    this._winText.x = centerX;
    this._winText.y = valueY - 5;
    this.addChild(this._winText);

    const winLabel = new Text({ text: 'WIN', style: winLabelStyle });
    winLabel.anchor.set(0.5, 0.5);
    winLabel.x = centerX;
    winLabel.y = labelY;
    this.addChild(winLabel);

    this._winDetailText = new Text({ text: '', style: winDetailStyle });
    this._winDetailText.anchor.set(0.5, 0);
    this._winDetailText.x = centerX;
    this._winDetailText.y = barHeight;
    this.addChild(this._winDetailText);

    // CASH (right)
    this._balanceText = new Text({ text: '$1000.00', style: valueStyle });
    this._balanceText.anchor.set(0.5, 0.5);
    this._balanceText.x = rightX;
    this._balanceText.y = valueY;
    this.addChild(this._balanceText);

    const cashLabel = new Text({ text: 'CASH', style: redLabelStyle });
    cashLabel.anchor.set(0.5, 0.5);
    cashLabel.x = rightX;
    cashLabel.y = labelY;
    this.addChild(cashLabel);

    // ================================================================
    // LOAD SPRITE ASSETS
    // ================================================================
    this._loadAssets(panelWidth, barHeight, leftX, centerX, rightX);

    logger.info('HUD initialized');
  }

  private async _loadAssets(
    pw: number, barH: number,
    leftX: number, centerX: number, rightX: number
  ): Promise<void> {
    try {
      const shadowTex = await Assets.load('assets/sprites/bottom_shadow.png') as Texture;
      const barTex = await Assets.load('assets/sprites/bottom_bg.png') as Texture;
      const panelTex = await Assets.load('assets/ui/total_bet_bg.png') as Texture;
      const winTex = await Assets.load('assets/sprites/win_bg.png') as Texture;

      let idx = 0;

      // Layer 0: Shadow — extends ABOVE the bar (negative y)
      if (shadowTex) {
        const shadow = new Sprite(shadowTex);
        shadow.anchor.set(0.5, 1);
        shadow.x = pw / 2;
        shadow.y = 0; // bottom of shadow = top of bar
        shadow.scale.set(pw / shadowTex.width);
        this.addChildAt(shadow, idx++);
      }

      // Layer 1: Gold bar — fills from y=0 to y=barH
      if (barTex) {
        const bar = new Sprite(barTex);
        bar.anchor.set(0.5, 0);
        bar.x = pw / 2;
        bar.y = 0;
        const sx = pw / barTex.width;
        const sy = barH / barTex.height;
        bar.scale.set(sx, sy);
        this.addChildAt(bar, idx++);
      }

      // Layer 2: Total Bet panel (left) — stretches full bar height
      if (panelTex) {
        const leftPanel = new Sprite(panelTex);
        leftPanel.anchor.set(0.5, 0.5);
        leftPanel.x = leftX;
        leftPanel.y = barH / 2;
        const psx = (pw * 0.28) / panelTex.width;
        const psy = (barH * 0.90) / panelTex.height; // stretch to fill bar height
        leftPanel.scale.set(psx, psy);
        this.addChildAt(leftPanel, idx++);

        // Layer 3: Cash panel (right) — same asset, same stretch
        const rightPanel = new Sprite(panelTex);
        rightPanel.anchor.set(0.5, 0.5);
        rightPanel.x = rightX;
        rightPanel.y = barH / 2;
        rightPanel.scale.set(psx, psy);
        this.addChildAt(rightPanel, idx++);
      }

      // Layer 4: Win panel (center) — larger, extends slightly above bar
      if (winTex) {
        const winPanel = new Sprite(winTex);
        winPanel.anchor.set(0.5, 0.5);
        winPanel.x = centerX;
        winPanel.y = barH / 2;
        const wsx = (pw * 0.42) / winTex.width;
        const wsy = (barH * 1.15) / winTex.height; // slightly taller than bar
        winPanel.scale.set(wsx, wsy);
        this.addChildAt(winPanel, idx++);
      }

      logger.info('HUD assets loaded');
    } catch (err) {
      logger.warn(`HUD asset load failed: ${err}`);
    }
  }

  get winDetailText(): Text { return this._winDetailText; }
  get winText(): Text { return this._winText; }
  setBalance(amount: number): void { this._balanceText.text = `$${amount.toFixed(2)}`; }
  setBet(amount: number): void { this._betText.text = `$${amount.toFixed(2)}`; }
  setWin(amount: number, _animate: boolean = false): void {
    this._winText.text = amount > 0 ? `$${amount.toFixed(2)}` : '';
  }
  setWinLabel(label: string): void { this._winText.text = label; }
  clearWin(): void { this._winText.text = ''; this._winDetailText.text = ''; }
  setWinDetail(text: string): void { this._winDetailText.text = text; }
}
